import { Injectable } from '@angular/core';
import * as DevicesActions from './devices.actions';
import { map, mergeMap, switchMap, take } from 'rxjs/operators';
import { DevicesPartialState } from './devices.reducer';
import { Actions, createEffect } from '@ngrx/effects';
import { DataPersistence, withCorrelationIdOf } from '@kion/ngrx-helper';
import { Device, DeviceSearch } from '@kion/data-models';
import { DevicesQuery } from './devices.selectors';
import { combineLatest, of } from 'rxjs';
import { ActionCache } from '../../../../../helper/ngrx-helper/src/lib/action-cache';
import { DeviceConnectors } from './device.connectors';

@Injectable()
export class DevicesEffects {
  search$ = createEffect(() =>
    this.dataPersistence.fetch(DevicesActions.search, {
      run: (action: ReturnType<typeof DevicesActions.search>) => {
        const searchOptions$ = combineLatest([
          this.dataPersistence.store.select(DevicesQuery.getPagination),
          this.dataPersistence.store.select(DevicesQuery.getQuery),
          this.dataPersistence.store.select(DevicesQuery.getActualSort),
        ]);
        return searchOptions$.pipe(
          take(1),
          switchMap(([pagination, stateQuerry, actualSort]) => {
            let query;
            // This  mixed mode should only run when a facet filter is selected, this is a hacky check with the :
            if (action.mixedMode && stateQuerry && stateQuerry.includes(':')) {
              query =
                action.query +
                stateQuerry.substring(
                  stateQuerry.indexOf(':'),
                  stateQuerry.length
                );
            } else if (action.query) {
              query = action.query;
            } else if (action.useState) {
              query = stateQuerry;
            } else {
              query = action.query;
            }
            return this.backend
              .search(
                query,
                action.useDefaultPage ? 1 : pagination.page,
                pagination.perPage,
                action.sort || actualSort
              )
              .pipe(
                map((response: DeviceSearch) =>
                  withCorrelationIdOf(
                    action,
                    DevicesActions.searchSuccess({
                      searchResult: { ...response, query },
                    })
                  )
                )
              );
          })
        );
      },
      onError: (action: ReturnType<typeof DevicesActions.search>, error) => {
        console.error('Error', error);
        return withCorrelationIdOf(
          action,
          DevicesActions.searchError({ error: error })
        );
      },
    })
  );
  loadById$ = createEffect(() =>
    this.dataPersistence.fetch(DevicesActions.loadById, {
      run: (action: ReturnType<typeof DevicesActions.loadById>) => {
        return this.dataPersistence.store
          .select(DevicesQuery.getAllDevicesById)
          .pipe(
            take(1),
            switchMap((devices) => {
              const runAction = this.backend
                .getDevice(action.factory, action.id)
                .pipe(
                  map((response: Device) =>
                    withCorrelationIdOf(
                      action,
                      DevicesActions.loadByIdSuccess({ device: response })
                    )
                  )
                );
              if (devices[action.id]) {
                return this.actionCache.withCache(
                  action,
                  () => runAction,
                  () =>
                    of(
                      withCorrelationIdOf(
                        action,
                        DevicesActions.loadByIdSuccess({
                          device: devices[action.id],
                        })
                      )
                    )
                );
              }
              return runAction;
            })
          );
      },
      onError: (action: ReturnType<typeof DevicesActions.loadById>, error) => {
        console.error('Error', error);
        return withCorrelationIdOf(
          action,
          DevicesActions.loadByIdError({ error: error })
        );
      },
    })
  );
  removeDevicesFromStore$ = createEffect(() =>
    this.dataPersistence.fetch(DevicesActions.removeDevicesFromStore, {
      run: (
        action: ReturnType<typeof DevicesActions.removeDevicesFromStore>
      ) => {
        return withCorrelationIdOf(
          action,
          DevicesActions.removeDevicesFromStoreSuccess({
            devices: action.devices,
          })
        );
      },
      onError: (
        action: ReturnType<typeof DevicesActions.removeDevicesFromStore>,
        error
      ) => {
        return withCorrelationIdOf(
          action,
          DevicesActions.removeDevicesFromStoreError({ error: error })
        );
      },
    })
  );
  updatePerPage$ = createEffect(() =>
    this.dataPersistence.fetch(DevicesActions.updatePerPage, {
      run: (action: ReturnType<typeof DevicesActions.updatePerPage>) => {
        const options$ = this.dataPersistence.store.select(
          DevicesQuery.getPagination
        );
        return options$.pipe(
          take(1),
          map((pagination) => {
            return withCorrelationIdOf(
              action,
              DevicesActions.updatePerPageSuccess({
                pagination: {
                  ...pagination,
                  perPage: action.perPage,
                  page: 1,
                },
              })
            );
          })
        );
      },
      onError: (
        action: ReturnType<typeof DevicesActions.updatePerPage>,
        error
      ) => {
        return withCorrelationIdOf(
          action,
          DevicesActions.updatePerPageError({ error: error })
        );
      },
    })
  );
  updatePage$ = createEffect(() =>
    this.dataPersistence.fetch(DevicesActions.updatePage, {
      run: (action: ReturnType<typeof DevicesActions.updatePage>) => {
        const options$ = this.dataPersistence.store.select(
          DevicesQuery.getPagination
        );
        return options$.pipe(
          take(1),
          map((pagination) => {
            return withCorrelationIdOf(
              action,
              DevicesActions.updatePageSuccess({
                pagination: {
                  ...pagination,
                  page: action.page,
                },
              })
            );
          })
        );
      },
      onError: (
        action: ReturnType<typeof DevicesActions.updatePage>,
        error
      ) => {
        return withCorrelationIdOf(
          action,
          DevicesActions.updatePageError({ error: error })
        );
      },
    })
  );
  updateSort$ = createEffect(() =>
    this.dataPersistence.fetch(DevicesActions.updateSort, {
      run: (action: ReturnType<typeof DevicesActions.updateSort>) => {
        const option$ = combineLatest([
          this.dataPersistence.store.select(DevicesQuery.getQuery),
        ]);
        return option$.pipe(
          take(1),
          mergeMap(([query]) => [
            withCorrelationIdOf(
              action,
              DevicesActions.updateSortSuccess({ sort: action.sort })
            ),
            withCorrelationIdOf(
              action,
              DevicesActions.search({
                query: query,
                sort: action.sort,
                useState: true,
                useDefaultPage: true,
              })
            ),
          ])
        );
      },
      onError: (
        action: ReturnType<typeof DevicesActions.updateSort>,
        error
      ) => {
        return withCorrelationIdOf(
          action,
          DevicesActions.updateSortError({ error: error })
        );
      },
    })
  );

  constructor(
    private actions$: Actions,
    private dataPersistence: DataPersistence<DevicesPartialState>,
    private backend: DeviceConnectors,
    private actionCache: ActionCache
  ) {}
}
